import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { getContext, publishFilter } from '@adornis/baseql/server/context.js';
import { CallRecording } from '@adornis/voice-assistant/call-recordings.js';
import { type AuthenticationContext, isAuthenticatedContext } from '../server/auth.js';
import { CallSession } from './CallSession.js';
import { CallSuccessReview } from './CallSuccessReview.js';
import {
  callOverviewPeriodCutoff,
  callOverviewRecordingFilter,
  callOverviewSearchPattern,
  callOverviewSessionFilter,
} from './call-origin-resolvers.js';

export const CALL_SUCCESS_STATUSES = ['yes', 'no', 'ignored'] as const;
type CallSuccessStatus = (typeof CALL_SUCCESS_STATUSES)[number];
interface CallSuccessReviewRow {
  _id: string;
  _class: string;
  callID: string;
  status: CallSuccessStatus;
  updatedAt: Date;
  updatedBy: string;
}
const CALL_SUCCESS_MAX_IDS = 500;
const CALL_SUCCESS_MAX_TIME_MS = 5000;

function requireCallSuccessUser(): string {
  const context = getContext() as AuthenticationContext | undefined;
  if (
    !context ||
    context.serverContext ||
    typeof context.userID !== 'string' ||
    !context.userID.trim() ||
    !isAuthenticatedContext(context)
  )
    throw new Error('CALL_SUCCESS_AUTH_REQUIRED');
  return context.userID;
}
function callSuccessText(value: unknown): string {
  if (Object.prototype.toString.call(value) !== '[object String]') throw new Error('CALL_SUCCESS_INVALID_ARGUMENT');
  return String(value);
}
function callSuccessID(value: unknown): string {
  const id = callSuccessText(value).trim();
  if (!id || id.length > 256 || /[\u0000-\u001f]/.test(id)) throw new Error('CALL_SUCCESS_INVALID_CALL');
  return id;
}
function andCallSuccessFilters(...filters: Record<string, unknown>[]): Record<string, unknown> {
  const present = filters.filter(filter => Object.keys(filter).length);
  return present.length > 1 ? { $and: present } : (present[0] ?? {});
}

export const getCallSuccessReviews = registerQuery({
  type: () => String,
  operationName: 'getCallSuccessReviews',
  params: [{ name: 'callIDs', type: () => [String] }],
  resolve: (callIDs: string[]) => async () => {
    requireCallSuccessUser();
    if (!Array.isArray(callIDs) || callIDs.length > CALL_SUCCESS_MAX_IDS) throw new Error('CALL_SUCCESS_INVALID_CALLS');
    const ids = [...new Set(callIDs.map(callSuccessID))];
    if (!ids.length) return '{}';
    const collection = await getCollection(CallSuccessReview);
    const rows = await collection
      .find({ _id: { $in: ids } }, { projection: { _id: 1, status: 1 }, maxTimeMS: CALL_SUCCESS_MAX_TIME_MS })
      .limit(ids.length)
      .toArray();
    const result: Record<string, CallSuccessStatus> = Object.create(null);
    for (const row of rows as unknown as CallSuccessReviewRow[]) {
      if (typeof row._id === 'string' && CALL_SUCCESS_STATUSES.includes(row.status)) result[row._id] = row.status;
    }
    return JSON.stringify(result);
  },
});

export const setCallSuccessReview = registerMutation({
  type: () => String,
  operationName: 'setCallSuccessReview',
  params: [
    { name: 'callID', type: () => String },
    { name: 'status', type: () => String },
  ],
  resolve: (callIDArg: string, statusArg: string) => async () => {
    const userID = requireCallSuccessUser();
    const callID = callSuccessID(callIDArg);
    const status = callSuccessText(statusArg);
    if (status !== '' && !CALL_SUCCESS_STATUSES.includes(status as CallSuccessStatus))
      throw new Error('CALL_SUCCESS_INVALID_STATUS');
    // Respect the same publish visibility as the overview, including recording-only calls.
    // Do not create a CallSession for an older call just to attach a manual review.
    const [sessions, recordings] = await Promise.all([getCollection(CallSession), getCollection(CallRecording)]);
    const [session, recording] = await Promise.all([
      sessions.findOne({ sessionID: callID }, { projection: { _id: 1 }, maxTimeMS: CALL_SUCCESS_MAX_TIME_MS }),
      recordings.findOne({ callID }, { projection: { _id: 1 }, maxTimeMS: CALL_SUCCESS_MAX_TIME_MS }),
    ]);
    if (!session && !recording) throw new Error('CALL_SUCCESS_CALL_NOT_FOUND');
    requireCallSuccessUser();
    const reviewVisibility = (await publishFilter(CallSuccessReview._class)) ?? {};
    const collection = await getRawCollection<CallSuccessReviewRow>(CallSuccessReview._collectionName);
    const filter = andCallSuccessFilters({ _id: callID }, reviewVisibility);
    const updatedAt = new Date();
    if (status === '') {
      await collection.deleteOne(filter, { maxTimeMS: CALL_SUCCESS_MAX_TIME_MS });
    } else {
      await collection.updateOne(
        filter,
        {
          $set: { callID, status: status as CallSuccessStatus, updatedAt, updatedBy: userID },
          $setOnInsert: { _id: callID, _class: CallSuccessReview._class },
        },
        { upsert: true, maxTimeMS: CALL_SUCCESS_MAX_TIME_MS },
      );
    }
    return JSON.stringify({ callID, status, updatedAt, updatedBy: userID });
  },
});

interface CallSuccessVisibility {
  sessions: Record<string, unknown>;
  recordings: Record<string, unknown>;
  reviews: Record<string, unknown>;
}
/** Single aggregate over metadata only. No page cap, log reads, or per-call queries.
 * Mongo's unique review _id makes the review join indexed and deterministic. */
export function buildCallSuccessStatsPipeline(
  search: unknown,
  period: unknown,
  visibility: CallSuccessVisibility,
  now = new Date(),
): Record<string, unknown>[] {
  const pattern = callOverviewSearchPattern(search);
  const cutoff = callOverviewPeriodCutoff(period, now);
  const countStatus = (status: CallSuccessStatus) => ({ $sum: { $cond: [{ $eq: ['$status', status] }, 1, 0] } });
  return [
    {
      $match: andCallSuccessFilters(visibility.sessions, callOverviewSessionFilter(pattern, cutoff), {
        sessionID: { $type: 'string', $ne: '' },
      }),
    },
    { $group: { _id: '$sessionID' } },
    {
      $unionWith: {
        coll: CallRecording._collectionName,
        pipeline: [
          {
            $match: andCallSuccessFilters(visibility.recordings, callOverviewRecordingFilter(pattern, cutoff), {
              callID: { $type: 'string', $ne: '' },
            }),
          },
          { $group: { _id: '$callID' } },
          {
            $lookup: {
              from: CallSession._collectionName,
              let: { callID: '$_id' },
              pipeline: [
                {
                  $match: andCallSuccessFilters(visibility.sessions, {
                    $expr: { $eq: ['$sessionID', '$$callID'] },
                  }),
                },
                { $project: { _id: 1 } },
                { $limit: 1 },
              ],
              as: 'existingSession',
            },
          },
          // A recording with any visible session is not an orphan, even when that
          // session falls outside the selected period or the overview's first 500 rows.
          { $match: { 'existingSession.0': { $exists: false } } },
          { $project: { _id: 1 } },
        ],
      },
    },
    {
      $lookup: {
        from: CallSuccessReview._collectionName,
        let: { callID: '$_id' },
        pipeline: [
          {
            $match: andCallSuccessFilters(visibility.reviews, {
              $expr: { $eq: ['$_id', '$$callID'] },
            }),
          },
          { $project: { _id: 0, status: 1 } },
          { $limit: 1 },
        ],
        as: 'review',
      },
    },
    { $project: { status: { $ifNull: [{ $arrayElemAt: ['$review.status', 0] }, ''] } } },
    {
      $group: {
        _id: null,
        yes: countStatus('yes'),
        no: countStatus('no'),
        ignored: countStatus('ignored'),
        total: { $sum: 1 },
      },
    },
    {
      $project: {
        _id: 0,
        yes: 1,
        no: 1,
        ignored: 1,
        total: 1,
        unreviewed: { $subtract: ['$total', { $add: ['$yes', '$no', '$ignored'] }] },
        rate: {
          $cond: [
            { $gt: [{ $add: ['$yes', '$no'] }, 0] },
            { $multiply: [{ $divide: ['$yes', { $add: ['$yes', '$no'] }] }, 100] },
            null,
          ],
        },
      },
    },
  ];
}

export const getCallSuccessStats = registerQuery({
  type: () => String,
  operationName: 'getCallSuccessStats',
  params: [
    { name: 'search', type: () => String },
    { name: 'period', type: () => String },
  ],
  resolve: (search: string, period: string) => async () => {
    requireCallSuccessUser();
    // BaseQL disallows aggregate on its client-context collection wrapper. Apply
    // each entity's actual publish filter explicitly before this bounded raw read.
    const [sessions, recordings, reviews] = await Promise.all([
      publishFilter(CallSession._class),
      publishFilter(CallRecording._class),
      publishFilter(CallSuccessReview._class),
    ]);
    const pipeline = buildCallSuccessStatsPipeline(search, period, {
      sessions: sessions ?? {},
      recordings: recordings ?? {},
      reviews: reviews ?? {},
    });
    const collection = await getRawCollection(CallSession._collectionName);
    const [counts] = await collection
      .aggregate(pipeline, { maxTimeMS: CALL_SUCCESS_MAX_TIME_MS, allowDiskUse: true })
      .toArray();
    return JSON.stringify(counts ?? { yes: 0, no: 0, ignored: 0, unreviewed: 0, total: 0, rate: null });
  },
});
